/**
 * LaunchBar — primary launch surfaces in the script editor.
 *
 * Four launch surfaces (left to right):
 *   1. "Author in Extension" → stages script in the user's installed v1.10
 *      authoring extension. Detects window.automatrixExtensionAvailable; if
 *      the extension isn't installed, shows an "Install Extension" affordance
 *      that links to the Chrome Web Store / extension-download endpoint.
 *   2. "Run as Learner" → server-side Chromium with the v4 learner extension
 *      loaded. Production-mirror runtime — what an actual learner Chromebook
 *      would do. Video persisted to run history.
 *   3. "Watch Mode" → live screencast + transport controls. Default engine:
 *      v4 (production-mirror live debugging). Chevron exposes the Playwright
 *      fallback for clean-baseline debugging when v3 platform code is noise.
 *      NOTE: Watch Mode v4 backend wiring is a follow-up (#137); the chevron
 *      currently routes both options to the existing pure-Playwright path —
 *      v4 selection logs the intent so we can ship the wiring next.
 *   4. "Run Headless" → background run, video saved to history. Chevron
 *      exposes engine choice (Playwright default, v4 learner ext).
 *
 * v5 (the Automatrix prototype extension) has been retired from the menus —
 * v4 is the strategic learner runtime going forward. Legacy v5 logs in run
 * history still render their chip via resolveExecutorMode for back-compat.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  ConnectedTv as WatchIcon,
  SmartDisplay as HeadlessIcon,
  ArrowDropDown as DropDownIcon,
  CloudQueue as PlaywrightIcon,
  CloudDownload as InstallIcon,
} from '@mui/icons-material';

const LaunchBar = ({
  onRunInExtension,
  onWatchMode,
  onRunHeadless,
}) => {
  // Detect whether the user has the v1.10 Automatrix authoring extension
  // installed. The extension sets window.automatrixExtensionAvailable on
  // load; we re-check on focus in case the user installs it then comes back.
  const [extInstalled, setExtInstalled] = useState(
    () => typeof window !== 'undefined' && !!window.automatrixExtensionAvailable,
  );
  useEffect(() => {
    const check = () => setExtInstalled(!!window.automatrixExtensionAvailable);
    check();
    window.addEventListener('focus', check);
    const interval = setInterval(check, 2000);
    return () => {
      window.removeEventListener('focus', check);
      clearInterval(interval);
    };
  }, []);

  // Watch Mode chevron — pick engine. Default 'extension-v4'.
  const watchAnchor = useRef(null);
  const [watchMenuOpen, setWatchMenuOpen] = useState(false);
  const fireWatch = (engine) => {
    setWatchMenuOpen(false);
    if (typeof onWatchMode === 'function') onWatchMode(engine);
  };

  // Run Headless chevron — pick engine. Default 'playwright'.
  const headlessAnchor = useRef(null);
  const [headlessMenuOpen, setHeadlessMenuOpen] = useState(false);
  const fireHeadless = (mode) => {
    setHeadlessMenuOpen(false);
    if (typeof onRunHeadless === 'function') onRunHeadless(mode);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#fafafa',
        flexWrap: 'wrap',
      }}
    >
      {extInstalled ? (
        <Tooltip title="Stage script in your installed Automatrix authoring extension (v1.10) — pick a credential, then click Play in the sidebar. Best for live editing + selector picking.">
          <Button
            variant="contained"
            size="small"
            startIcon={<ExtensionIcon />}
            onClick={onRunInExtension}
            sx={{
              bgcolor: '#4caf50',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#388e3c' },
            }}
          >
            Author in Extension
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title="Install the Automatrix authoring extension (v1.10) to author + record + edit scripts in your browser. Without it, this button is unavailable.">
          <Button
            variant="outlined"
            size="small"
            startIcon={<InstallIcon />}
            onClick={() => window.open('/extension-download', '_blank')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#4caf50',
              color: '#4caf50',
              '&:hover': { bgcolor: 'rgba(76,175,80,0.04)', borderColor: '#388e3c' },
            }}
          >
            Install Extension to Author
          </Button>
        </Tooltip>
      )}

      <Tooltip title="Run on the server in the AchieveDXP v4 learner extension — production-mirror runtime (v3 app bar, blocker, etc. all loaded). Video persisted to run history.">
        <Button
          variant="contained"
          size="small"
          startIcon={<ExtensionIcon />}
          onClick={() => fireHeadless('extension-v4')}
          sx={{
            bgcolor: '#2da99e',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: '#1f8a80' },
          }}
        >
          Run as Learner
        </Button>
      </Tooltip>

      <ButtonGroup
        ref={watchAnchor}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': { bgcolor: 'rgba(25,118,210,0.04)', borderColor: '#1565c0' },
          },
        }}
      >
        <Tooltip title="Live screencast + transport controls. Default engine is the v4 learner extension (production-mirror). Chevron lets you pick a clean Playwright baseline.">
          <Button startIcon={<WatchIcon />} onClick={() => fireWatch('extension-v4')}>
            Watch Mode
          </Button>
        </Tooltip>
        <Tooltip title="Pick Watch Mode engine">
          <Button
            size="small"
            sx={{ px: 0.5, minWidth: 28 }}
            aria-label="select watch engine"
            onClick={() => setWatchMenuOpen((v) => !v)}
          >
            <DropDownIcon fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <Menu
        anchorEl={watchAnchor.current}
        open={watchMenuOpen}
        onClose={() => setWatchMenuOpen(false)}
      >
        <MenuItem onClick={() => fireWatch('extension-v4')} dense>
          <ListItemIcon><ExtensionIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Watch via v4 (Learner)"
            secondary="Production-mirror — v3 app bar + blocker visible"
          />
        </MenuItem>
        <MenuItem onClick={() => fireWatch('playwright')} dense>
          <ListItemIcon><PlaywrightIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Watch via Playwright"
            secondary="Clean baseline — no extension, fastest"
          />
        </MenuItem>
      </Menu>

      <ButtonGroup
        ref={headlessAnchor}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#757575',
            color: '#757575',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', borderColor: '#616161' },
          },
        }}
      >
        <Tooltip title="Run on the server in the background — Playwright by default. Use the chevron to pick the v4 learner extension instead.">
          <Button startIcon={<HeadlessIcon />} onClick={() => fireHeadless('playwright')}>
            Run Headless
          </Button>
        </Tooltip>
        <Tooltip title="Pick headless engine">
          <Button
            size="small"
            sx={{ px: 0.5, minWidth: 28 }}
            aria-label="select headless engine"
            onClick={() => setHeadlessMenuOpen((v) => !v)}
          >
            <DropDownIcon fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <Menu
        anchorEl={headlessAnchor.current}
        open={headlessMenuOpen}
        onClose={() => setHeadlessMenuOpen(false)}
      >
        <MenuItem onClick={() => fireHeadless('playwright')} dense>
          <ListItemIcon><PlaywrightIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Run Headless (Playwright)"
            secondary="Server-side, no extension — fastest"
          />
        </MenuItem>
        <MenuItem onClick={() => fireHeadless('extension-v4')} dense>
          <ListItemIcon><ExtensionIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Run Headless (v4 Learner)"
            secondary="Server-side, production-mirror"
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LaunchBar;
